/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: exam-level report rollup — port of the handoff viewer's
// `applyExamLevelReportRollup` (pcg_viewer_v05_build.py, v0.8.3.4–v0.8.3.10).
//
// Upstream ran this in the viewer at export time over the server's
// per-recording preview evidence. Here it runs in the report adapter over the
// review-export snapshot, whose entries carry each recording's full evidence
// package, so the rollup is deterministic for a given snapshot and re-runs on
// every rebase.
//
// Timing inputs are the values the reviewer saw and accepted in the viewer —
// each recording's package (evidence.heartRate.valueBpm, which carries the
// viewer's half/double correction where one applied, and
// evidence.systolicDiastolicTiming.{rawSdRatio, sysMs, diaMs}, from the
// viewer's selected cycles). Upstream v0.8.3.18 stores the same values with
// the acceptance and merges them over its server preview before rolling up.
// The analysis pipeline's whole-recording metrics, carried on each snapshot
// entry as `pipelineMetrics` (hrBpm, sdRatio, sysMs, diaMs, rrCv, nS1,
// orientationScore, gateStatus …), fill any value the package lacks, decide
// eligibility for a package without a rate (gate not rejecting, a finite
// rate, at least 8 detected S1s and an S1/S2 orientation score of at least
// 0.25 — upstream's preview rule), and are kept whole in the audit block
// (`pipelineHeartRateBpm` per recording) for comparison.
//
// A recording whose guide carries the timing engine has ONE canonical timing
// result: its Evaluation Range's, as its package carries it. Beats outside
// the range never contribute, so the pipeline's whole-recording metrics
// neither fill its values (heart rate, SYS:DIA, durations, cycle variability)
// nor decide its eligibility: it is timing-eligible exactly when its package
// carries the range's heart rate (none when the engine produced no range).
// Such a package is recognised by its `evaluationRange` provenance block (the
// entry's `evaluationRange`, from the package's top level), which travels on
// the recording's timing source record and its audit entry; a snapshot from
// before the block existed is recognised by its Evaluation Range heart-rate
// source. Pipeline numbers stay informational there (the technical rationale's
// S1 marker count and S1/S2 confidence, `pipelineHeartRateBpm` in the audit).
//
// Acceptance is per evidence domain: a recording feeds the timing blocks only
// when it was accepted for Timing, and can be the Structural Sound source
// only when accepted for Structural Sound. Recordings accepted before domains
// existed count for both.
//
// A heart rate the reviewer confirmed in the viewer's note dialog belongs to
// the recording it was entered on (the snapshot entry's `hcpConfirmedBpm`,
// from its disposition) and counts only when that recording is accepted for
// Timing. It replaces that recording's rate in the roll-up inputs — the
// "package values first" slot of `timingBasisOf` — so the range, the medians,
// the ±10 % cluster and the half/double check all run on it: a one-recording
// exam collapses to the single confirmed value, as upstream's exam-wide
// override does, while a multi-recording exam keeps the accepted range over
// the (partly confirmed) rates. The automated rate it replaced stays on the
// record (`automatedValueBpm`) and, when the included recordings resolve to
// one confirmed value, on the Heart Rate block with a review note when the
// two disagree. A confirmed value that happens to agree clears nothing by
// fiat: `reviewRequired` is judged on the substituted rates.
//
// Rules kept verbatim from upstream, pinned by
// fixtures/exam-rollup-upstream-goldens.json (generated by running the
// upstream function itself):
// - Heart rate: every recording accepted for Timing stays in the summary —
//   one value is shown as a single value, several as the min–max range of
//   all of them (v0.8.3.18 at git d5ca637; earlier builds dropped the values
//   outside the cluster). The largest cluster in which every value is within
//   ±10% of the cluster median, ties broken by dispersion, is advisory: it is
//   the `proposedCluster`, and the accepted values outside it are the
//   `discrepant` recordings. `reviewRequired` is raised for a tied best
//   cluster, a 2-of-2 disagreement, a 2-of-3 cluster, a sub-75% majority, or
//   any discrepant value; suspected half/double counts are listed in the
//   audit block and never corrected, divided or multiplied.
// - Sys/dia timing: median over the included recordings.
// - Cycle Regularity: the mean timing variation over the same included
//   recordings (upstream at git ff7b05c; the median before that).
// - Each timing block also lists every included recording's own numbers
//   (`sourceRecordings`, upstream v0.8.3.18): rate, sys/dia timing, timing
//   spread, usable cycles, guide quality and S1/S2 confidence, with a short
//   technical rationale — the report's timing cards offer these as the
//   recordings the reviewer may include or leave out per card.
// - Signal Quality: describes the recordings that actually contribute (the
//   included recordings plus the Structural Sound source); the lowest one
//   limits.
// - S1/S2 intensity follows the Structural Sound source; it is position-
//   specific supporting evidence and is never averaged across sites.
// - Export is invariant to the recording that happened to be selected.
//
// Deliberate deviations from upstream:
// - Contributors are the caller's choice (this platform: accepted recordings;
//   upstream v0.8.3.6+ also counted "reviewed by listening"). Like upstream,
//   there is no roll-up without contributors: the adapter refuses the export
//   before this runs, so an empty contributor list here only means every
//   contributor lacked evidence.
// - One included timing recording is worded as one recording, not "across
//   recordings" (and, for snapshots without pipeline metrics, keeps its own
//   package blocks whole).
// - Cycle Regularity takes the mean of the fields the report's card grades
//   on (the package's selected-cycle timing spread and its bands), as
//   upstream does since v0.8.3.18 (`robustSpreadPct`; never the pipeline's
//   whole-recording cycle variability, kept here for audit only as
//   `pipelineCycleVariability`). Where upstream averages the one spread
//   value, every field of the block is averaged here — nothing is left over
//   from a single recording — and the card's own per-recording thresholds
//   grade it.
// - Signal Quality ranks the viewer's own signal label (Reliable / Fair /
//   Unreliable — Reliable when both the timing and the energy guides are
//   usable) over the contributing recordings; the pipeline's guide-quality
//   tier is kept in the finding and audit. Upstream v0.8.3.18 ranks its
//   server preview's `clinicalLabel` instead, which is Reliable only when
//   timing is available AND the murmur-triage probability is at least 0.5
//   (Fair for one of the two, worded "Fair / Limited") — a recording without
//   a suspected murmur can never read Reliable there.
// - Structural Sound: the report's card is defined by the package's
//   cycle-repeatability summary, so this port selects the most significant
//   *package* summary (status severity, then cycle repeatability, then
//   energy) and carries that recording's summary AND detail blocks whole —
//   no averaging, no phase borrowing. Upstream ranks the same way since
//   v0.8.3.18 (classification, then cycle repeatability) and writes its own
//   wording; since pcg-viewer-mvp git 8b4d874 it no longer requires a
//   murmur-triage probability of a candidate (the probability is a last
//   tie-break defaulting to 0, which its pipeline never supplies). Recordings
//   without a scored structural result are not candidates; when no recording
//   accepted for Structural Sound has one, the summary reads "Not assessed"
//   with a grey status (upstream at pcg-viewer-mvp git 6b385e4; never normal,
//   and no longer an amber result), naming the first such recording as its
//   source, and the report leaves the card for a manual assessment.
// - Candidate evidence (upstream at git 6b385e4): usable evidence of a domain
//   on a recording the reviewer accepted for the other domain only is not
//   discarded. It is summarised under `candidateEvidence.timing` /
//   `candidateEvidence.structuralSound` (state "pending_hcp_review"), which
//   the report offers as a pending card — to review and Save, edit or
//   Exclude — when nothing was accepted for that domain. It never enters
//   the evidence blocks. Upstream's candidates are the recordings it marks
//   reviewed by listening as well as the accepted ones; this platform has
//   only acceptance. A Structural Sound candidate needs a scored result, as
//   an accepted source does (upstream since git 8b4d874: a usable recording
//   whose structural basis has a scored cycle percentage), and carries its
//   own recording's summary whole. Cycle Regularity of the Timing candidates
//   is rolled field by field, as for accepted recordings.
// - `reviewRequired` is what the Medaica Report acts on: the timing cards
//   arrive in the report as pending, with the complete accepted range shown,
//   until the reviewer confirms the range, replaces it with a clinician
//   value, or goes back to the review to change which recordings are
//   accepted for Timing (Heart Report v0.5.8 at cardiac-panel-v4 git
//   31d729a). The reason is worded here as a statement of what the accepted
//   recordings did, and stays on the provider document and the Exam Source
//   summary after confirmation.

import { STRUCTURAL_SOUND_ROLLUP_OBSERVED } from "./constants"
import {
  HCP_ENTERED_VIEWER_SOURCE,
  MIXED_AUTOMATED_SOURCE,
  VIEWER_CURATED_CYCLES_SOURCE,
  VIEWER_EVAL_RANGE_SOURCE,
  automatedHeartRateSourceOf,
  durationStatisticOf,
  sharedDurationStatistic,
} from "./timing-sources"
import {
  pcgTimingDisplayLabel,
  rhythmRegularityStatusFromPattern,
  timingPatternFromRegularityInputs,
} from "./timing-pattern"

// v2: every accepted Timing value stays in the heart-rate range (the cluster
// is advisory). v3: the timing blocks carry a per-recording record for each
// included Timing recording (`sourceRecordings`), which the report's timing
// cards use to let the reviewer choose the recordings each card includes.
// v4: an unscored Structural Sound summary is "Not assessed" (grey), and
// unaccepted but usable evidence travels as `candidateEvidence`. v5: every
// recording accepted for Structural Sound is listed on the summary block.
// v6: a reviewer-confirmed heart rate substitutes per recording
// (`heartRateSource`, `hcpConfirmedBpm` on the records and the Heart Rate
// block), the records carry marker provenance, and the Structural Sound
// wording is the viewer's. v7: the reviewer observations became internal
// review context with a separate Report note, and the bump rebases drafts so
// their notes are re-imported under that rule (Report wording edited earlier
// becomes the Report note); a recording whose timing engine selected an
// Evaluation Range names its automated heart rate as the range's
// (`viewer_eval_range`, with `automatedSource` on the records, the Heart Rate
// block and the audit), takes its timing from the range alone (never filled
// from the pipeline's whole-recording metrics) and carries the range's
// provenance (`evaluationRange` on its record and audit entry). A draft whose
// roll-up predates the current schema is rebased when opened.
export const EXAM_ROLLUP_SCHEMA = "medaica.pcg.exam-report-rollup.v7"

/** Whether a report's roll-up was computed by the current rules (a report without one is left alone). */
export function isRollupCurrent(report) {
  const rollup = report && report.examRollup
  return !rollup || rollup.schema === EXAM_ROLLUP_SCHEMA
}

export const EXAM_ROLLUP_TIMING_RULE =
  "all HCP-accepted Timing values remain included; the largest ±10% coherent cluster is advisory and discrepancies require explicit HCP resolution"
export const EXAM_ROLLUP_STRUCTURAL_RULE =
  "most significant reportable finding among accepted recordings; no averaging and no phase borrowing"
const STRUCTURAL_LIMITED_FINDING =
  "Structural-sound energy could not be classified because no scored structural-sound cycles were available. This must not be interpreted as a normal result."
const STRUCTURAL_DETECTED_PATIENT_TEXT =
  "This screening looks for extra sound patterns between or around the usual heart sounds that repeat across multiple heartbeats. These patterns can have many causes and are not, by themselves, a diagnosis."
export const NO_TIMING_ACCEPTED_FINDING = "No recording was accepted for Timing."
export const NO_STRUCTURAL_SOUND_ACCEPTED_FINDING = "No recording was accepted for Structural Sound."
export const NO_EVIDENCE_ACCEPTED_FINDING = "No recording was accepted for Timing or Structural Sound."
// What the summary reads when no accepted recording could be scored
export const STRUCTURAL_NOT_ASSESSED_OBSERVED = "Not assessed"
export const STRUCTURAL_ASSESSMENT_LIMITED_PATIENT_TEXT =
  "The recording did not provide enough usable information to assess additional heart-sound patterns."
const STRUCTURAL_LIMITED_RULE =
  "accepted Structural Sound evidence requires scored structural-sound cycles; unavailable evidence is never converted to normal"
export const CANDIDATE_PENDING_STATE = "pending_hcp_review"
export const STRUCTURAL_CANDIDATE_REVIEW_REASON =
  "Structural Sound data was available but was not accepted in Viewer. Review, edit, Save, or Exclude this card."
const CONFIDENCE_POLICY =
  "Metric eligibility is assessed separately for each report source; timing is summarized across every recording accepted for Timing and Structural Sound uses the most significant reportable finding."

const deepCopy = (value) => JSON.parse(JSON.stringify(value))
// Strict: null / undefined / "" are absent values, never 0 (Number(null) is 0).
const finite = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))

/** A reviewer-confirmed heart rate as stored: a whole number 25–220, else null. */
export function normalizeConfirmedBpm(value) {
  if (!finite(value)) return null
  const bpm = Number(value)
  return bpm >= 25 && bpm <= 220 ? Math.round(bpm) : null
}
/** How the automated heart rate is named wherever it is compared with a confirmed one. */
export const AUTOMATED_ESTIMATE_LABEL = "Automated estimate from the selected usable cycles"
/** The same, for a rate the timing engine measured over its Evaluation Range (upstream's wording). */
export const AUTOMATED_EVAL_RANGE_ESTIMATE_LABEL = "Automated Eval Range estimate"
// The name of the automated rate behind a summary: the Evaluation Range's,
// the selected usable cycles', or a neutral one when the rates behind it
// come from both
const automatedEstimateLabel = (source) => {
  if (source === VIEWER_EVAL_RANGE_SOURCE) return AUTOMATED_EVAL_RANGE_ESTIMATE_LABEL
  if (source === MIXED_AUTOMATED_SOURCE) return "Automated estimate"
  return AUTOMATED_ESTIMATE_LABEL
}
// Whether two rates stand in a half/double relation (ratio 1.8–2.2)
const halfDoubleRelated = (a, b) => {
  const ratio = Math.max(a, b) / Math.max(Math.min(a, b), 1)
  return ratio >= 1.8 && ratio <= 2.2
}
/**
 * The Heart Rate review note for a confirmed value: a disagreement of more
 * than 10 bpm or a half/double relation asks the report to keep both values;
 * more than 3 bpm is noted as slight; closer agreement needs no note. The
 * automated rate is named as the Evaluation Range's when `automatedSource`
 * says it came from one.
 */
export function confirmedBpmReviewNote(confirmedBpm, automatedBpm, automatedSource) {
  if (confirmedBpm === null || automatedBpm === null) return null
  const delta = Math.abs(confirmedBpm - automatedBpm)
  if (delta <= 3) return null
  const estimate =
    automatedSource === VIEWER_EVAL_RANGE_SOURCE ? "the automated Eval Range estimate" : "the automated estimate"
  return delta > 10 || halfDoubleRelated(confirmedBpm, automatedBpm)
    ? `HCP-entered BPM differs from ${estimate} by ${Math.round(delta)} bpm; retain both values in evidence details.`
    : `HCP-entered BPM differs slightly from ${estimate} by ${Math.round(delta)} bpm.`
}
const average = (values) => values.reduce((sum, value) => sum + Number(value), 0) / values.length
const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}
// Sum of relative deviations from the cluster median (upstream's tie-break).
const dispersion = (items) => {
  const center = median(items.map((item) => item.bpm))
  return items.reduce((sum, item) => sum + Math.abs(item.bpm - center) / Math.max(center, 1), 0)
}
const coherent = (items) => {
  if (!items.length) return false
  const center = median(items.map((item) => item.bpm))
  return items.every((item) => Math.abs(item.bpm - center) / Math.max(center, 1) <= 0.1)
}
const STATUS_RANK = { red: 2, amber: 1, green: 0 }
const SIGNAL_RANK = { unreliable: 1, fair: 2, reliable: 3 }
const GUIDE_QUALITY_RANK = { UNKNOWN: 0, LOW: 1, MODERATE: 2, HIGH: 3 }
const BOTH_DOMAINS = { timing: true, structuralSound: true }
// Callers that predate domain acceptance pass no domains: both count.
const domainsOf = (item) => item.acceptedDomains || BOTH_DOMAINS
const numberOrNull = (value) => (finite(value) ? Number(value) : null)

/** Upstream's preview-evidence rule for "useful S1-to-S1 timing is available". */
export function pipelineTimingEligible(metrics) {
  if (!metrics) return false
  const gate = String(metrics.gateStatus || "").toLowerCase()
  return (
    gate !== "reject" &&
    gate !== "rejected" &&
    finite(metrics.hrBpm) &&
    Number(metrics.nS1 || 0) >= 8 &&
    Number(metrics.orientationScore || 0) >= 0.25
  )
}

// The heart rate the reviewer confirmed on a recording, if any: the snapshot
// entry's own field, else one a package already carries
const confirmedBpmOf = (item) => {
  const heartRate = (item.evidence && item.evidence.heartRate) || {}
  return normalizeConfirmedBpm(item.hcpConfirmedBpm ?? heartRate.hcpConfirmedBpm)
}
// The viewer's own rate for a recording: a package whose heartRate block was
// already rewritten with a confirmed value keeps the automated rate alongside
const viewerBpmOf = (evidence) => {
  const heartRate = (evidence && evidence.heartRate) || {}
  if (finite(heartRate.automatedValueBpm)) return Number(heartRate.automatedValueBpm)
  if (finite(heartRate.hcpConfirmedBpm)) return null
  return finite(heartRate.valueBpm) ? Number(heartRate.valueBpm) : null
}
// A timing-engine recording's Evaluation Range provenance, as a record or
// audit entry carries it; nothing for any other recording
const evaluationRangeEntry = (item) => (item.evaluationRange ? { evaluationRange: deepCopy(item.evaluationRange) } : {})
// Whether a recording's package comes from a timing-engine guide: it carries
// the Evaluation Range provenance block, or (a snapshot from before the block)
// names its automated heart rate as the Evaluation Range's
const timingEnginePackage = (item) =>
  Boolean(item.evaluationRange) || automatedHeartRateSourceOf(item.evidence?.heartRate) === VIEWER_EVAL_RANGE_SOURCE
// The numbers a recording brings to the timing roll-up, or null when it has
// none: the package's own values — what the viewer displayed when the
// recording was accepted — with the pipeline's whole-recording metrics
// filling whatever the package leaves out. Eligibility stays the pipeline's
// (the same rule the Accept dialog offers Timing on), so a recording the
// pipeline could not time contributes nothing even if its package has a
// rate; a snapshot without pipeline metrics is eligible on the package's.
// A timing-engine package is the exception: its values are the Evaluation
// Range's alone, never filled from the pipeline, and it is eligible exactly
// when it carries the range's heart rate.
// With `confirmedBpmApplies` (the recording is accepted for Timing) a rate
// the reviewer confirmed on the recording takes the place of the viewer's;
// the rate it replaced is kept as `automatedBpm`. `automatedSource` says
// where the package's automated rate came from — the timing engine's
// Evaluation Range when the package says so, else the selected usable
// cycles — so each recording is labelled by its own guide. `durationStatistic`
// says what its SYS / DIA durations are (the timing engine's medians, or the
// usable cycles' means); null when they come from the pipeline's metrics.
const timingBasisOf = (item, { confirmedBpmApplies = false } = {}) => {
  const engine = timingEnginePackage(item)
  // The pipeline's metrics, where they may stand in for the package's
  const metrics = engine ? null : item.pipelineMetrics || null
  if (metrics && !pipelineTimingEligible(metrics)) return null
  const viewerBpm = viewerBpmOf(item.evidence)
  if (engine && viewerBpm === null) return null
  const confirmedBpm = confirmedBpmApplies ? confirmedBpmOf(item) : null
  const block = timingBlock(item.evidence)
  const fill = (value, key) => (finite(value) ? Number(value) : metrics ? numberOrNull(metrics[key]) : null)
  const pipelineBpm = metrics ? numberOrNull(metrics.hrBpm) : null
  if (confirmedBpm !== null || viewerBpm !== null) {
    const automatedSource = automatedHeartRateSourceOf(item.evidence?.heartRate) || VIEWER_CURATED_CYCLES_SOURCE
    return {
      source: "package",
      heartRateSource: confirmedBpm !== null ? HCP_ENTERED_VIEWER_SOURCE : automatedSource,
      automatedSource,
      bpm: confirmedBpm !== null ? confirmedBpm : viewerBpm,
      hcpConfirmedBpm: confirmedBpm,
      automatedBpm: viewerBpm !== null ? viewerBpm : pipelineBpm,
      sdRatio: fill(block.rawSdRatio, "sdRatio"),
      sysMs: fill(block.sysMs, "sysMs"),
      diaMs: fill(block.diaMs, "diaMs"),
      durationStatistic: finite(block.sysMs) || finite(block.diaMs) ? durationStatisticOf(block) : null,
      rrCv: metrics ? numberOrNull(metrics.rrCv) : null,
    }
  }
  if (!metrics) return null
  return {
    source: "pipeline",
    heartRateSource: "pipeline",
    automatedSource: null,
    bpm: Number(Number(metrics.hrBpm).toFixed(1)),
    hcpConfirmedBpm: null,
    automatedBpm: Number(Number(metrics.hrBpm).toFixed(1)),
    sdRatio: numberOrNull(metrics.sdRatio),
    sysMs: numberOrNull(metrics.sysMs),
    diaMs: numberOrNull(metrics.diaMs),
    durationStatistic: null,
    rrCv: numberOrNull(metrics.rrCv),
  }
}
const timingBlock = (evidence) => (evidence && (evidence.systolicDiastolicTiming || evidence.cardiacCycleTiming)) || {}
const heartRateBpm = (evidence) => {
  const value = evidence && evidence.heartRate ? evidence.heartRate.valueBpm : null
  return finite(value) ? Number(value) : null
}
// The S1/S2 marker provenance of a recording's timing, from its package's
// regularity block (upstream `timingMarkerProvenanceScore`): a score from the
// share of inferred markers — 1 at none, 0 from a third upward — else the
// marker confidence; null when the package carries neither.
const markerProvenanceOf = (item) => {
  const regularity = (item.evidence && item.evidence.rhythmRegularity) || {}
  const inferredMarkerPct = numberOrNull(regularity.inferredMarkerPct)
  const markerConfidence = numberOrNull(regularity.markerConfidence)
  const markerProvenanceScore =
    inferredMarkerPct !== null ? Math.max(0, 1 - Math.min(1, (inferredMarkerPct / 100) * 3)) : markerConfidence
  return { inferredMarkerPct, markerConfidence, markerProvenanceScore }
}
// A summary is reportable only when the recording has a scored
// cycle-repeatability result (upstream: accepted Structural Sound evidence
// requires scored structural-sound cycles). Energy observed with its phase
// withheld, and an assessment limited by too few guide-quality cycles or an
// unreliable input, are not candidates: with nothing scored the exam-level
// card says so rather than reporting either as a finding.
export const structurallyAssessable = (evidence) => {
  const summary = evidence && evidence.structuralSoundSummary
  if (!summary || !summary.status || !summary.observed || summary.observed === "Not assessed") return false
  return finite(summary.maxCyclePct)
}
const roundBpm = (value) => Math.round(value)
// What the SYS / DIA durations summarized over `items` are: the statistic all
// of those with durations share ("median" / "mean"), else null
const durationStatisticAcross = (items) =>
  sharedDurationStatistic(
    items
      .filter((item) => finite(item.basis.sysMs) || finite(item.basis.diaMs))
      .map((item) => item.basis.durationStatistic)
  )

/**
 * Heart-rate cluster selection across timing-eligible contributors. Exposed
 * for tests; `applyExamLevelReportRollup` is the entry point.
 */
export function buildExamTimingRollup(contributors) {
  const timingAccepted = contributors.filter((item) => domainsOf(item).timing)
  const candidates = timingAccepted
    .map((item) => ({ ...item, basis: timingBasisOf(item, { confirmedBpmApplies: true }) }))
    .filter((item) => item.basis)
    .map((item) => ({ ...item, bpm: item.basis.bpm }))
  // Every coherent subset, largest first, then the tightest (upstream's
  // brute force; exams hold a handful of recordings).
  let clusters = []
  for (let mask = 1; mask < 1 << candidates.length; mask += 1) {
    const items = candidates.filter((_, index) => mask & (1 << index))
    if (coherent(items)) clusters.push(items)
  }
  const largestSize = clusters.reduce((best, items) => Math.max(best, items.length), 0)
  clusters = clusters.filter((items) => items.length === largestSize)
  const clusterKey = (items) =>
    items
      .map((item) => item.recordingId)
      .sort()
      .join("|")
  const uniqueClusters = [...new Map(clusters.map((items) => [clusterKey(items), items])).values()]
  const rankedClusters = uniqueClusters.sort((a, b) => dispersion(a) - dispersion(b))
  const proposedCluster = rankedClusters[0] || []
  const tiedBest =
    rankedClusters.length > 1 && Math.abs(dispersion(rankedClusters[0]) - dispersion(rankedClusters[1])) < 0.0001
  // Explicit acceptance for Timing is authoritative: every candidate is in
  // the summary. The cluster only says which of them disagree with the rest.
  const included = candidates
  const discrepant = candidates.filter((item) => !proposedCluster.some((kept) => kept.recordingId === item.recordingId))
  const insufficientConsensus = candidates.length === 2 && proposedCluster.length < 2
  const weakThreeRecordConsensus = candidates.length === 3 && proposedCluster.length === 2
  const noMajority = candidates.length >= 3 && proposedCluster.length < Math.ceil(candidates.length * 0.75)
  const reviewRequired =
    tiedBest || insufficientConsensus || weakThreeRecordConsensus || noMajority || discrepant.length > 0
  const rates = included.map((item) => item.bpm)
  const roundedRates = rates.map(roundBpm)
  const minRate = roundedRates.length ? Math.min(...roundedRates) : null
  const maxRate = roundedRates.length ? Math.max(...roundedRates) : null
  const observed = !roundedRates.length
    ? "Not assessed"
    : minRate === maxRate
    ? `${minRate} bpm`
    : `${minRate}–${maxRate} bpm`
  const rateCenter = rates.length ? median(rates) : null
  // A discrepant value is judged against the cluster it fell outside of
  const proposedCenter = proposedCluster.length ? median(proposedCluster.map((item) => item.bpm)) : rateCenter
  const proposedRates = proposedCluster.map((item) => roundBpm(item.bpm))
  const halfDoubleIds = discrepant
    .filter((item) => {
      if (!proposedCenter) return false
      const ratio = item.bpm / proposedCenter
      return (ratio >= 0.45 && ratio <= 0.55) || (ratio >= 1.8 && ratio <= 2.2)
    })
    .map((item) => item.recordingId)
  // Marker provenance of the agreeing recordings (medians), and of each
  // suspected half/double count against them: a suspect whose markers are
  // markedly weaker — a score more than 0.20 below, or more than 20 points
  // more inferred markers — is the likelier miscount. Review only; nothing
  // is corrected.
  const medianOf = (values) => (values.length ? median(values) : null)
  const proposedProvenance = proposedCluster.map(markerProvenanceOf)
  const proposedMarkerProvenanceScore = medianOf(
    proposedProvenance.map((entry) => entry.markerProvenanceScore).filter((value) => value !== null)
  )
  const proposedInferredMarkerPct = medianOf(
    proposedProvenance.map((entry) => entry.inferredMarkerPct).filter((value) => value !== null)
  )
  const halfDoubleReviewFindings = discrepant
    .filter((item) => halfDoubleIds.includes(item.recordingId))
    .map((item) => {
      const { markerProvenanceScore, inferredMarkerPct } = markerProvenanceOf(item)
      const weakerMarkerProvenance =
        (markerProvenanceScore !== null &&
          proposedMarkerProvenanceScore !== null &&
          markerProvenanceScore + 0.2 < proposedMarkerProvenanceScore) ||
        (inferredMarkerPct !== null &&
          proposedInferredMarkerPct !== null &&
          inferredMarkerPct > proposedInferredMarkerPct + 20)
      return {
        recordingId: item.recordingId,
        position: item.position || null,
        valueBpm: Number(item.bpm.toFixed(1)),
        proposedClusterBpm: Number(proposedCenter.toFixed(1)),
        markerProvenanceScore,
        inferredMarkerPct,
        proposedClusterMarkerProvenanceScore: proposedMarkerProvenanceScore,
        proposedClusterInferredMarkerPct: proposedInferredMarkerPct,
        weakerMarkerProvenance,
        reason: weakerMarkerProvenance
          ? "cross_position_half_double_with_weaker_marker_provenance"
          : "cross_position_half_double_requires_hcp_review",
      }
    })
  const halfDoubleWeakerMarkerProvenanceIds = halfDoubleReviewFindings
    .filter((item) => item.weakerMarkerProvenance)
    .map((item) => item.recordingId)
  const aggregate = (key) => {
    const values = included
      .map((item) => item.basis[key])
      .filter(finite)
      .map(Number)
    return values.length ? median(values) : null
  }
  const sources = new Set(candidates.map((item) => item.basis.source))
  // The confirmed heart rate of the summary: the one value the included
  // recordings resolve to — a single recording's, or several recordings'
  // when every one of them was confirmed at the same rate — else null; the
  // automated rate(s) it replaced alongside, for the card's review note
  const confirmed = included.filter((item) => item.basis.hcpConfirmedBpm !== null)
  const confirmedValues = [...new Set(confirmed.map((item) => item.basis.hcpConfirmedBpm))]
  const hcpConfirmedBpm =
    included.length > 0 && confirmed.length === included.length && confirmedValues.length === 1
      ? confirmedValues[0]
      : null
  const automatedValueBpm =
    hcpConfirmedBpm === null
      ? null
      : medianOf(confirmed.map((item) => item.basis.automatedBpm).filter((value) => value !== null))
  const automatedValueRounded = automatedValueBpm === null ? null : Number(automatedValueBpm.toFixed(1))
  // Where the automated rate(s) behind the summary came from: the confirmed
  // recordings' when the summary is a confirmed value, else the included
  // ones'; "mixed" when they differ, null when no package rate is behind it
  const automatedSourceSet = new Set(
    (hcpConfirmedBpm !== null ? confirmed : included).map((item) => item.basis.automatedSource).filter(Boolean)
  )
  let automatedSource = null
  if (automatedSourceSet.size > 1) automatedSource = MIXED_AUTOMATED_SOURCE
  else if (automatedSourceSet.size === 1) automatedSource = [...automatedSourceSet][0]
  const reviewReasons = []
  if (tiedBest) reviewReasons.push("tied_best_cluster")
  if (insufficientConsensus) reviewReasons.push("two_recordings_disagree")
  if (weakThreeRecordConsensus) reviewReasons.push("two_of_three")
  if (noMajority) reviewReasons.push("no_majority")
  if (discrepant.length) reviewReasons.push("discrepant_recordings")
  return {
    candidates,
    included,
    proposedCluster,
    proposedRangeBpm: proposedRates.length
      ? { min: Math.min(...proposedRates), max: Math.max(...proposedRates) }
      : null,
    discrepant,
    halfDoubleIds,
    tiedBest,
    reviewRequired,
    reviewReasons,
    observed,
    rates,
    rateCenter,
    valueBpm: rates.length ? Number(median(rates).toFixed(1)) : null,
    rangeBpm: rates.length ? { min: minRate, max: maxRate } : null,
    sdRatio: aggregate("sdRatio"),
    sysMs: aggregate("sysMs"),
    diaMs: aggregate("diaMs"),
    durationStatistic: durationStatisticAcross(included),
    rrCv: aggregate("rrCv"),
    // Which numbers fed the cluster: the pipeline's uncorrected metrics, the
    // package's values (older snapshots), or null when nothing was eligible.
    inputSet: sources.size === 0 ? null : sources.size > 1 ? "mixed" : [...sources][0],
    halfDoubleReviewFindings,
    halfDoubleWeakerMarkerProvenanceIds,
    markerProvenanceScore: proposedMarkerProvenanceScore,
    inferredMarkerPct: proposedInferredMarkerPct,
    confirmedRecordingIds: confirmed.map((item) => item.recordingId),
    hcpConfirmedBpm,
    acceptedSource:
      hcpConfirmedBpm !== null ? HCP_ENTERED_VIEWER_SOURCE : automatedSource || VIEWER_CURATED_CYCLES_SOURCE,
    automatedSource,
    automatedValueBpm: automatedValueRounded,
    confirmedBpmReviewNote: confirmedBpmReviewNote(hcpConfirmedBpm, automatedValueRounded, automatedSource),
    bpmDelta:
      hcpConfirmedBpm !== null && automatedValueRounded !== null
        ? Number(Math.abs(hcpConfirmedBpm - automatedValueRounded).toFixed(1))
        : null,
    halfDoubleRelation:
      hcpConfirmedBpm !== null && automatedValueRounded !== null
        ? halfDoubleRelated(hcpConfirmedBpm, automatedValueRounded)
        : false,
    // Recordings were accepted, but none of them for Timing.
    noTimingAccepted: contributors.length > 0 && timingAccepted.length === 0,
  }
}

const recordingLabel = (item) => item.position || item.recordingId
const joinList = (items) =>
  items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}` : items.join("")
const rangeText = (range) => (range && range.min !== range.max ? `${range.min}–${range.max} bpm` : `${range.min} bpm`)

/**
 * What the Heart Rate card says about a flagged summary: a statement of what
 * the accepted recordings did — which of them disagree with the others and
 * at what rate, or that the recordings do not settle on a rate — closing
 * with the fact that every accepted value is kept. Empty when nothing is
 * flagged.
 */
export function describeTimingReview(timing) {
  const outliers = timing.discrepant.map(
    (item) =>
      `${recordingLabel(item)} (${roundBpm(item.bpm)} bpm${
        timing.halfDoubleIds.includes(item.recordingId) ? ", suspected half/double count" : ""
      })`
  )
  const agreeing = timing.proposedCluster.map(recordingLabel)
  const agreeingText = agreeing.length
    ? ` ${agreeing.length === 1 ? "" : "the ±10% agreement of "}${joinList(agreeing)} (${rangeText(
        timing.proposedRangeBpm
      )})`
    : ""
  const kept = `${
    timing.included.length === 2 ? "Both" : "All"
  } accepted values are kept in the range; the ±10% agreement is advisory only.${
    (timing.halfDoubleWeakerMarkerProvenanceIds || []).length
      ? " Possible half/double-count source has weaker marker provenance."
      : ""
  }`
  const reasons = timing.reviewReasons
  if (reasons.includes("two_recordings_disagree")) {
    return `The two accepted recordings do not agree on a heart rate within ±10% (${joinList(
      timing.included.map((item) => `${recordingLabel(item)} ${roundBpm(item.bpm)} bpm`)
    )}). ${kept}`
  }
  if (timing.tiedBest) {
    return `Two equally coherent groups of the accepted recordings give different heart rates${
      outliers.length ? `; outside the larger group: ${joinList(outliers)}` : ""
    }. ${kept}`
  }
  if (reasons.includes("two_of_three") || reasons.includes("no_majority")) {
    return `Only ${timing.proposedCluster.length} of ${
      timing.candidates.length
    } accepted recordings agree on a heart rate within ±10%: ${joinList(outliers)} ${
      outliers.length === 1 ? "differs" : "differ"
    } from${agreeingText}. ${kept}`
  }
  if (!outliers.length) return ""
  return `Heart rate varied between the accepted recordings: ${joinList(outliers)} ${
    outliers.length === 1 ? "falls" : "fall"
  } outside${agreeingText}. ${kept}`
}

/**
 * Most significant reportable Structural Sound finding; null when none is
 * assessable. Ranked by status, then cycle repeatability, then exam order
 * (upstream sorts by classification and cycle percentage; its last
 * tie-break, a murmur-model probability defaulting to 0, is never supplied
 * by its pipeline, so ties fall to exam order).
 */
export function selectStructuralSoundSource(contributors) {
  const candidates = contributors.filter(
    (item) => domainsOf(item).structuralSound && structurallyAssessable(item.evidence)
  )
  return rankStructural(candidates)[0] || null
}

// Numeric fields of the package's rhythmRegularity block, all of which the
// report's Cycle Regularity card reads; each is rolled as a mean.
const REGULARITY_NUMERIC_FIELDS = [
  "regularityPct",
  "timingIndex",
  "cycleVariability",
  "successiveDifferencePct",
  "lag1Autocorrelation",
  "timingVariabilityPct",
  "robustSpreadPct",
  "timingSwingPct",
  "robustSpreadMs",
  "timingSwingMs",
  "medianCycleMs",
  "expectedNormalVariabilityPct",
  "expectedNormalSwingPct",
  "irregularVariabilityPct",
  "irregularSwingPct",
  "markerConfidence",
]

/**
 * One included Timing recording's own numbers, as the report's timing cards
 * list them when the reviewer chooses which recordings each card includes
 * (upstream `timingSourceRecord`). `item` is a timing candidate from
 * `buildExamTimingRollup` (its `basis` and `bpm` set). The numbers come from
 * the recording's timing basis — the package's accepted values, pipeline
 * metrics filling any it lacks — and the timing spread and marker
 * provenance from the package's rhythmRegularity block; anything unknown is
 * null. `heartRateSource` says where the rate came from (a value the
 * reviewer confirmed on the recording, the timing engine's Evaluation Range,
 * the viewer's rate over its selected cycles, or the pipeline), with the
 * confirmed value, the automated rate it replaced and that rate's source
 * (`automatedSource`, null for a pipeline rate) alongside. The rationale names the usable S1 markers, the
 * guide quality and the S1/S2 confidence when the pipeline metrics carry
 * them, and otherwise only says that timing evidence is available; it is
 * informational, never a timing value. `evaluationRange` is the Evaluation
 * Range provenance of a timing-engine recording's package (absent otherwise).
 */
export function timingSourceRecord(item) {
  const basis = item.basis || {}
  const metrics = item.pipelineMetrics || {}
  const regularity = (item.evidence && item.evidence.rhythmRegularity) || {}
  const provenance = markerProvenanceOf(item)
  const guideQuality = String(metrics.guideQuality || "unknown").toUpperCase()
  const cycles = numberOrNull(metrics.nS1)
  const orientation = numberOrNull(metrics.orientationScore)
  const rationale =
    [
      cycles !== null ? `${cycles} usable S1 markers` : null,
      guideQuality !== "UNKNOWN" ? `${guideQuality.toLowerCase()} timing guide` : null,
      orientation !== null ? `S1/S2 confidence ${orientation.toFixed(2)}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "Timing evidence available"
  return {
    recordingId: item.recordingId,
    position: item.position || null,
    valueBpm: item.bpm,
    heartRateSource: basis.heartRateSource || (basis.source === "pipeline" ? "pipeline" : VIEWER_CURATED_CYCLES_SOURCE),
    automatedSource: basis.automatedSource ?? null,
    hcpConfirmedBpm: basis.hcpConfirmedBpm ?? null,
    automatedValueBpm: basis.automatedBpm ?? null,
    sdRatio: numberOrNull(basis.sdRatio),
    sysMs: numberOrNull(basis.sysMs),
    diaMs: numberOrNull(basis.diaMs),
    robustSpreadPct: numberOrNull(regularity.robustSpreadPct),
    rrCv: numberOrNull(basis.rrCv),
    usableCycles: cycles,
    guideQuality,
    orientationScore: orientation,
    markerConfidence: provenance.markerConfidence,
    inferredMarkerPct: provenance.inferredMarkerPct,
    markerProvenanceScore: provenance.markerProvenanceScore,
    technicalRationale: rationale,
    ...evaluationRangeEntry(item),
  }
}

/**
 * Cycle Regularity over the included timing recordings — the same recordings
 * that produced the heart rate. One recording keeps its own block; several
 * are averaged field by field (the mean timing variation is the authoritative
 * value, upstream at git ff7b05c), so no value is left over from one of them.
 */
export function rollUpCycleRegularity(existing, timing, reviewRequired) {
  const included = timing.included
  const ids = included.map((item) => item.recordingId)
  const sourceRecordings = included.map(timingSourceRecord)
  const constants = {
    normalRange: existing && existing.normalRange,
    displayReferenceRange: existing && existing.displayReferenceRange,
    basis: existing && existing.basis,
  }
  const blocks = included.map((item) => item.evidence && item.evidence.rhythmRegularity).filter(Boolean)
  const unassessed = (finding) => ({
    ...constants,
    ...Object.fromEntries(REGULARITY_NUMERIC_FIELDS.map((field) => [field, null])),
    observed: "Not assessed",
    status: "amber",
    sourceRecordingId: null,
    sourcePosition: null,
    sourceRecordingIds: ids,
    sourceRecordings,
    pipelineCycleVariability: timing.rrCv,
    finding,
    reviewRequired,
  })
  if (!included.length) {
    return unassessed(
      timing.noTimingAccepted
        ? NO_TIMING_ACCEPTED_FINDING
        : "Cycle regularity could not be summarized from accepted Timing recordings."
    )
  }
  if (included.length === 1 && blocks.length === 1) {
    return {
      ...deepCopy(blocks[0]),
      sourceRecordingId: included[0].recordingId,
      sourcePosition: included[0].position || blocks[0].sourcePosition || null,
      sourceRecordingIds: ids,
      sourceRecordings,
      pipelineCycleVariability: timing.rrCv,
      reviewRequired,
    }
  }
  const rolled = Object.fromEntries(
    REGULARITY_NUMERIC_FIELDS.map((field) => {
      const values = blocks
        .map((block) => block[field])
        .filter(finite)
        .map(Number)
      return [field, values.length ? average(values) : null]
    })
  )
  if (!finite(rolled.timingSwingPct)) {
    return unassessed("Cycle regularity could not be summarized from accepted Timing recordings.")
  }
  const pattern = timingPatternFromRegularityInputs(
    rolled.regularityPct,
    rolled.cycleVariability,
    rolled.markerConfidence,
    rolled.timingVariabilityPct,
    rolled.expectedNormalVariabilityPct,
    rolled.irregularVariabilityPct,
    rolled.timingSwingPct,
    rolled.expectedNormalSwingPct,
    rolled.irregularSwingPct
  )
  const displayPattern = pcgTimingDisplayLabel(pattern)
  const metrics = [...new Set(blocks.map((block) => block.metricUsed || block.metric_used).filter(Boolean))]
  const metricUsed = metrics.length === 1 ? metrics[0] : null
  const swing = Number(rolled.timingSwingPct).toFixed(1)
  return {
    ...constants,
    ...rolled,
    observed: `±${swing}% timing variation${displayPattern ? ` · ${displayPattern}` : ""}`,
    status: rhythmRegularityStatusFromPattern(pattern, "amber"),
    sourceRecordingId: null,
    sourcePosition: null,
    sourceRecordingIds: ids,
    sourceRecordings,
    pipelineCycleVariability: timing.rrCv,
    // Upstream's one-line finding; the rolled spread in milliseconds and the
    // median cycle length stay on the block for the card's own notes
    finding: `Cycle regularity summarized across ${included.length} accepted Timing recording${
      included.length === 1 ? "" : "s"
    }; mean selected-cycle timing variation ±${swing}%.`,
    timingLabel: displayPattern || null,
    rhythmPattern: pattern || null,
    displayRhythmPattern: displayPattern || null,
    // Per-recording qualifications do not describe a summary of several
    timingConfidence: null,
    confidenceRationaleCodes: [],
    confidenceRationaleText: "",
    timingMethodFlags: [],
    metricUsed,
    metric_used: metricUsed,
    rateRange: null,
    outlierCount: null,
    reviewRequired,
  }
}

const STRUCTURAL_UNAVAILABLE_REASON = "No scored structural-sound cycles were available from this recording."

/**
 * One recording accepted for Structural Sound, as the report's card lists
 * every such recording (upstream `structuralSourceRecords`, git ff7b05c):
 * whether it can support the result (a scored cycle-repeatability summary),
 * and otherwise why not, with its classification and cycle percentages.
 * `selected` marks the recording the exam-level result was taken from.
 */
export function structuralSourceRecord(item, selectedRecordingId = null) {
  const summary = (item.evidence && item.evidence.structuralSoundSummary) || {}
  const assessable = structurallyAssessable(item.evidence)
  return {
    recordingId: item.recordingId,
    position: item.position || null,
    assessable,
    unavailableReason: assessable ? null : summary.finding || STRUCTURAL_UNAVAILABLE_REASON,
    classification: summary.status || null,
    maxCyclePct: numberOrNull(summary.maxCyclePct),
    sysCyclePct: numberOrNull(summary.sysCyclePct),
    diaCyclePct: numberOrNull(summary.diaCyclePct),
    scoredCycles: numberOrNull(summary.scoredCycles),
    selected: !!selectedRecordingId && item.recordingId === selectedRecordingId,
  }
}

// The same ranking an accepted Structural Sound source is chosen by
const rankStructural = (items) =>
  [...items].sort((a, b) => {
    const rank = (item) => {
      const summary = item.evidence.structuralSoundSummary
      return {
        status: STATUS_RANK[summary.status] ?? -1,
        cycles: finite(summary.maxCyclePct) ? Number(summary.maxCyclePct) : -1,
        sequence: Number(item.recordingSequence) || 0,
      }
    }
    const ra = rank(a)
    const rb = rank(b)
    return rb.status - ra.status || rb.cycles - ra.cycles || ra.sequence - rb.sequence
  })

/** The exam-level Structural Sound wording for a result's status. */
export const structuralObservedForStatus = (status) =>
  STRUCTURAL_SOUND_ROLLUP_OBSERVED[status] || STRUCTURAL_SOUND_ROLLUP_OBSERVED.green
const structuralObservedFor = structuralObservedForStatus
// A summary's status as the exam-level card grades it: anything but red or amber is green
const reportableStatus = (status) => (status === "red" || status === "amber" ? status : "green")

// Pairs of timing items whose rates stand in a half/double relationship
const halfDoublePairsOf = (items) => {
  const pairs = []
  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      const low = Math.min(items[i].bpm, items[j].bpm)
      const high = Math.max(items[i].bpm, items[j].bpm)
      const ratio = high / Math.max(low, 1)
      if (ratio >= 1.8 && ratio <= 2.2) pairs.push([items[i].recordingId, items[j].recordingId])
    }
  }
  return pairs
}

// Why a Timing candidate needs a look, or null when its recordings agree
const candidateReviewReason = (halfDoublePairs, spread) => {
  if (halfDoublePairs.length) {
    return "Possible half/double heart-rate relationship across reviewed recordings; no value was silently discarded."
  }
  if (spread > 0.1) return "Reviewed recordings differ by more than 10%; all observed values are shown for HCP review."
  return null
}

// A candidate heart rate is green only when its recordings agree on a normal resting rate
const candidateRateStatus = (reviewRequired, center) =>
  !reviewRequired && center >= 60 && center <= 100 ? "green" : "amber"

/**
 * Timing evidence the reviewer did not accept, on recordings accepted for
 * Structural Sound only: the range of their rates, their median sys/dia
 * timing and their rolled Cycle Regularity, as a candidate the report shows
 * pending. Null when no such recording has usable timing. A spread above
 * 10% or a half/double pair is flagged; no value is dropped.
 */
export function buildTimingCandidate(contributors) {
  const items = contributors
    .filter((item) => !domainsOf(item).timing)
    .map((item) => ({ ...item, basis: timingBasisOf(item) }))
    .filter((item) => item.basis)
    .map((item) => ({ ...item, bpm: item.basis.bpm }))
  if (!items.length) return null
  const rates = items.map((item) => item.bpm)
  const rounded = rates.map(roundBpm)
  const min = Math.min(...rounded)
  const max = Math.max(...rounded)
  const observed = min === max ? `${min} bpm` : `${min}–${max} bpm`
  const center = median(rates)
  const spread = (max - min) / Math.max(center, 1)
  const halfDoublePairs = halfDoublePairsOf(items)
  const reviewRequired = spread > 0.1 || halfDoublePairs.length > 0
  const reviewReason = candidateReviewReason(halfDoublePairs, spread)
  const ids = items.map((item) => item.recordingId)
  const records = items.map(timingSourceRecord)
  const aggregate = (key) => {
    const values = items
      .map((item) => item.basis[key])
      .filter(finite)
      .map(Number)
    return values.length ? median(values) : null
  }
  const sdRatio = aggregate("sdRatio")
  const count = items.length
  const scopeText = `${count} recording${count === 1 ? "" : "s"} accepted for Structural Sound only`
  return {
    state: CANDIDATE_PENDING_STATE,
    sourceRecordingIds: ids,
    recordings: records,
    reviewRequired,
    reviewReason,
    halfDoublePairs,
    heartRate: {
      observed,
      valueBpm: Number(center.toFixed(1)),
      rangeBpm: { min, max },
      status: candidateRateStatus(reviewRequired, center),
      sourceRecordingIds: ids,
      sourceRecordings: records,
      reviewRequired,
      finding: `Unaccepted Timing candidate across ${scopeText}: ${observed}.`,
      patientText: `Heart rate across recordings: ${observed}.`,
    },
    cardiacCycleTiming: {
      observed: finite(sdRatio) && Number(sdRatio) > 0 ? `1 : ${(1 / Number(sdRatio)).toFixed(1)}` : "Not assessed",
      rawSdRatio: sdRatio,
      sysMs: aggregate("sysMs"),
      diaMs: aggregate("diaMs"),
      durationStatistic: durationStatisticAcross(items),
      status: "amber",
      sourceRecordingIds: ids,
      reviewRequired,
      finding: `Unaccepted Timing candidate from ${scopeText}.`,
    },
    rhythmRegularity: {
      ...rollUpCycleRegularity(null, { included: items, rrCv: aggregate("rrCv"), noTimingAccepted: false }, false),
      reviewRequired,
      finding: `Unaccepted Cycle Regularity candidate from ${scopeText}.`,
    },
  }
}

/**
 * Structural Sound evidence the reviewer did not accept, on recordings
 * accepted for Timing only: the most significant scored result among them,
 * as a candidate the report shows pending. Null when none is scored.
 */
export function buildStructuralSoundCandidate(contributors) {
  const items = rankStructural(
    contributors.filter((item) => !domainsOf(item).structuralSound && structurallyAssessable(item.evidence))
  )
  if (!items.length) return null
  const candidate = items[0]
  const summary = deepCopy(candidate.evidence.structuralSoundSummary)
  const position = candidate.position || summary.sourcePosition || null
  const status = reportableStatus(summary.status)
  return {
    state: CANDIDATE_PENDING_STATE,
    sourceRecordingIds: items.map((item) => item.recordingId),
    reviewRequired: true,
    reviewReason: STRUCTURAL_CANDIDATE_REVIEW_REASON,
    summary: {
      ...summary,
      observed: structuralObservedFor(status),
      status,
      sourceRecordingId: candidate.recordingId,
      sourcePosition: position,
      finding: `Unaccepted Structural Sound candidate from ${
        position || "a recording accepted for Timing only"
      }. Review required before inclusion.`,
      patientText: null,
    },
  }
}

/**
 * Signal Quality of the evidence actually in the report: the recordings in
 * the heart-rate summary plus the Structural Sound source. The lowest
 * contributing signal label limits the exam; its own block is carried so the
 * label, status and allowances stay one recording's.
 */
export function rollUpSignalQuality(existing, sources) {
  const graded = sources
    .map((item) => ({ item, block: item.evidence && item.evidence.signalQuality }))
    .filter((entry) => entry.block && entry.block.observed)
    .map((entry) => ({ ...entry, rank: SIGNAL_RANK[String(entry.block.observed).toLowerCase()] ?? SIGNAL_RANK.fair }))
  const ids = sources.map((item) => item.recordingId)
  if (!graded.length) {
    return {
      ...(existing || {}),
      observed: "Not assessed",
      status: "amber",
      sourceRecordingId: null,
      sourcePosition: null,
      sourceRecordingIds: ids,
      lowestGuideQuality: null,
      finding: NO_EVIDENCE_ACCEPTED_FINDING,
    }
  }
  const lowest = [...graded].sort(
    (a, b) => a.rank - b.rank || (Number(a.item.recordingSequence) || 0) - (Number(b.item.recordingSequence) || 0)
  )[0]
  const tiers = sources
    .map((item) => String((item.pipelineMetrics && item.pipelineMetrics.guideQuality) || "").toUpperCase())
    .filter((tier) => tier in GUIDE_QUALITY_RANK && tier !== "UNKNOWN")
  const lowestTier = tiers.length ? [...tiers].sort((a, b) => GUIDE_QUALITY_RANK[a] - GUIDE_QUALITY_RANK[b])[0] : null
  const position = lowest.item.position || lowest.block.sourcePosition || null
  return {
    ...deepCopy(lowest.block),
    sourceRecordingId: sources.length === 1 ? lowest.item.recordingId : null,
    sourcePosition: sources.length === 1 ? position : null,
    sourceRecordingIds: ids,
    lowestRecordingId: lowest.item.recordingId,
    lowestGuideQuality: lowestTier,
    finding:
      sources.length === 1
        ? lowest.block.finding
        : `Signal quality reflects the ${
            sources.length
          } recordings contributing evidence to this report; lowest contributing signal quality: ${
            lowest.block.observed
          }${position ? ` (${position})` : ""}.${
            lowestTier ? ` Lowest pipeline guide-quality tier: ${lowestTier}.` : ""
          }`,
  }
}

/**
 * Rewrites the exam-level blocks of a handoff package in place.
 *
 * `recordings`: every recording of the exam —
 *   { recordingId, position, recordingSequence, reviewStatus, evidence | null,
 *     contributes, acceptedDomains?, pipelineMetrics?, hcpConfirmedBpm? }
 * where `evidence` is the recording's package `evidence` block (null when the
 * recording has none), `evaluationRange` is the package's Evaluation Range
 * provenance block (a timing-engine guide's; null or omitted otherwise),
 * `contributes` is the caller's contributor rule,
 * `acceptedDomains` ({ timing, structuralSound }; omitted = both) says which
 * evidence domains were accepted, `pipelineMetrics` carries the analysis
 * pipeline's whole-recording timing metrics when the snapshot has them, and
 * `hcpConfirmedBpm` is the heart rate the reviewer confirmed on the
 * recording (null or omitted when none).
 */
export function applyExamLevelReportRollup(
  pkg,
  recordings,
  { now = () => new Date().toISOString(), contributorRule = "accepted_evidence_domains" } = {}
) {
  const all = recordings || []
  const contributors = all.filter((item) => item.contributes && item.evidence)
  const timing = buildExamTimingRollup(contributors)
  const reviewRequired = timing.reviewRequired
  const reviewReason = describeTimingReview(timing)
  // Every included recording's own timing numbers, listed on each timing
  // block so the report's cards can offer them for per-card inclusion
  const timingSourceRecords = timing.included.map(timingSourceRecord)
  // What the Heart Rate card needs to show the complete range and, when
  // flagged, to let the reviewer resolve it
  const heartRateRange = {
    sourceRecordings: timingSourceRecords,
    proposedClusterRecordingIds: timing.proposedCluster.map((item) => item.recordingId),
    proposedClusterRangeBpm: timing.proposedRangeBpm,
    discrepantRecordings: timing.discrepant.map((item) => ({
      recordingId: item.recordingId,
      position: item.position || null,
      valueBpm: item.bpm,
      ...markerProvenanceOf(item),
    })),
    halfDoubleReviewFindings: timing.halfDoubleReviewFindings,
    halfDoubleWeakerMarkerProvenanceIds: timing.halfDoubleWeakerMarkerProvenanceIds,
    // The confirmed heart rate when the included recordings resolve to one,
    // where it came from, and the automated rate it replaced
    hcpConfirmedBpm: timing.hcpConfirmedBpm,
    acceptedSource: timing.acceptedSource,
    automatedSource: timing.automatedSource,
    automatedObserved: timing.automatedValueBpm !== null ? `${Math.round(timing.automatedValueBpm)} bpm` : null,
    automatedValueBpm: timing.automatedValueBpm,
    reviewNote: timing.confirmedBpmReviewNote,
    bpmDelta: timing.bpmDelta,
    halfDoubleRelation: timing.halfDoubleRelation,
  }
  // The Heart Rate sentences for a confirmed value: what the reviewer entered,
  // the automated rate it replaced, and the review note when they disagree
  const confirmedFinding =
    timing.hcpConfirmedBpm === null
      ? null
      : [
          `Accepted BPM entered in Viewer: ${timing.hcpConfirmedBpm} bpm.`,
          timing.automatedValueBpm !== null
            ? `${automatedEstimateLabel(timing.automatedSource)}: ${Math.round(timing.automatedValueBpm)} bpm.`
            : "",
          timing.confirmedBpmReviewNote || "",
        ]
          .filter(Boolean)
          .join(" ")
  const includedIds = timing.included.map((item) => item.recordingId)
  const isIncluded = (id) => includedIds.includes(id)
  const structuralSource = selectStructuralSoundSource(contributors)
  // Set when Structural Sound was accepted but no accepted recording was scorable
  let limitedStructuralSourceId = null
  const evidence = pkg.evidence || (pkg.evidence = {})

  // ---- Timing blocks --------------------------------------------------
  if (timing.included.length === 1 && !timing.included[0].pipelineMetrics) {
    // One timing contributor from a snapshot written before pipeline metrics
    // existed: its own package blocks, annotated
    const owner = timing.included[0]
    ;["heartRate", "timingSummary", "systolicDiastolicTiming", "cardiacCycleTiming"].forEach((key) => {
      if (!owner.evidence[key]) return
      evidence[key] = { ...deepCopy(owner.evidence[key]), sourceRecordingIds: includedIds, reviewRequired }
      if (key !== "timingSummary") evidence[key].sourceRecordings = timingSourceRecords
    })
    evidence.heartRate = {
      ...evidence.heartRate,
      rangeBpm: timing.rangeBpm,
      ...heartRateRange,
      reviewReason: reviewRequired ? reviewReason : null,
    }
    if (confirmedFinding) {
      // The confirmed value stands in for the package's own rate
      Object.assign(evidence.heartRate, {
        observed: timing.observed,
        valueBpm: timing.valueBpm,
        status: timing.rateCenter >= 60 && timing.rateCenter <= 100 ? "green" : "amber",
        finding: confirmedFinding,
        patientText: `Heart rate: ${timing.observed}.`,
      })
    }
    if (reviewRequired) evidence.heartRate.status = "amber"
  } else {
    const observedRatio =
      finite(timing.sdRatio) && Number(timing.sdRatio) > 0
        ? `1 : ${(1 / Number(timing.sdRatio)).toFixed(1)}`
        : "Not assessed"
    const count = timing.included.length
    const soleSource = count === 1 ? timing.included[0] : null
    const soleLabel = soleSource && soleSource.position ? ` (${soleSource.position})` : ""
    // The durations are named as medians or means only when every recording
    // behind them reports the same statistic
    const durationsAdjective = timing.durationStatistic ? `${timing.durationStatistic} ` : ""
    const notSummarized = timing.noTimingAccepted
      ? NO_TIMING_ACCEPTED_FINDING
      : "Heart rate could not be summarized from the reviewed recordings."
    evidence.heartRate = {
      ...(evidence.heartRate || {}),
      observed: timing.observed,
      valueBpm: timing.valueBpm,
      rangeBpm: timing.rangeBpm,
      status: reviewRequired ? "amber" : timing.rateCenter >= 60 && timing.rateCenter <= 100 ? "green" : "amber",
      sourceRecordingId: soleSource ? soleSource.recordingId : null,
      sourcePosition: soleSource ? soleSource.position || null : null,
      sourceRecordingIds: includedIds,
      ...heartRateRange,
      finding: !timing.rates.length
        ? notSummarized
        : confirmedFinding ??
          (soleSource
            ? `Heart rate ${timing.observed} from one accepted Timing recording${soleLabel}.`
            : `Heart rate across recordings: ${timing.observed}.`),
      patientText: !timing.rates.length
        ? "Heart rate was not available for this report."
        : soleSource || confirmedFinding
        ? `Heart rate: ${timing.observed}.`
        : `Heart rate across recordings: ${timing.observed}.`,
      reviewRequired,
      reviewReason: reviewRequired ? reviewReason : null,
    }
    evidence.timingSummary = {
      ...(evidence.timingSummary || {}),
      observed: timing.observed,
      sourceRecordingId: null,
      sourcePosition: null,
      sourceRecordingIds: includedIds,
      finding: timing.rates.length
        ? `Timing summary across ${count} accepted Timing recording${count === 1 ? "" : "s"}; heart-rate range ${
            timing.observed
          }.`
        : timing.noTimingAccepted
        ? NO_TIMING_ACCEPTED_FINDING
        : "Timing summary unavailable.",
      reviewRequired,
    }
    const durationMs = (value) => (finite(value) ? Math.round(value) : "—")
    let cardiacCycleFinding = "Cardiac cycle timing could not be summarized from the reviewed recordings."
    if (timing.rates.length) {
      const scope = soleSource ? "" : ` (median across ${count} recordings)`
      cardiacCycleFinding = `Cardiac cycle timing ${observedRatio}${scope}; ${durationsAdjective}systole ${durationMs(
        timing.sysMs
      )} ms and diastole ${durationMs(timing.diaMs)} ms.`
    } else if (timing.noTimingAccepted) {
      cardiacCycleFinding = NO_TIMING_ACCEPTED_FINDING
    }
    ;["systolicDiastolicTiming", "cardiacCycleTiming"].forEach((key) => {
      evidence[key] = {
        ...(evidence[key] || {}),
        observed: observedRatio,
        status:
          finite(timing.sdRatio) && Number(timing.sdRatio) >= 0.4 && Number(timing.sdRatio) <= 0.67 ? "green" : "amber",
        sourceRecordingId: null,
        sourcePosition: null,
        sourceRecordingIds: includedIds,
        sourceRecordings: timingSourceRecords,
        rawSdRatio: timing.sdRatio,
        sysMs: timing.sysMs,
        diaMs: timing.diaMs,
        durationStatistic: timing.durationStatistic,
        finding: cardiacCycleFinding,
        reviewRequired,
      }
    })
  }

  // ---- Structural Sound ----------------------------------------------
  // Every recording accepted for Structural Sound, in review order, for the
  // report's source list — the selected one and the ones that cannot
  // support the result, with the reason
  const acceptedStructuralSources = [...contributors]
    .filter((item) => domainsOf(item).structuralSound && item.evidence)
    .sort((a, b) => (Number(a.recordingSequence) || 0) - (Number(b.recordingSequence) || 0))
  const structuralSourceRecords = acceptedStructuralSources.map((item) =>
    structuralSourceRecord(item, structuralSource ? structuralSource.recordingId : null)
  )
  const structuralSourceIds = acceptedStructuralSources.map((item) => item.recordingId)
  if (structuralSource) {
    const summary = deepCopy(structuralSource.evidence.structuralSoundSummary)
    const position = structuralSource.position || summary.sourcePosition || null
    // The source recording's cycle-repeatability status decides the card;
    // the wording is the exam-level summary's, not the recording's own
    const status = summary.status === "red" ? "red" : summary.status === "amber" ? "amber" : "green"
    const detected = status !== "green"
    evidence.structuralSoundSummary = {
      ...summary,
      observed: structuralObservedFor(status),
      status,
      sourceRecordingId: structuralSource.recordingId,
      sourcePosition: position,
      sourceRecordingIds: structuralSourceIds,
      sourceRecordings: structuralSourceRecords,
      detailStatus: "available_from_same_source",
      selectionRule: EXAM_ROLLUP_STRUCTURAL_RULE,
      finding: detected
        ? `The strongest reportable Structural Sound pattern was seen at ${
            position || "the source recording"
          }. Screening observation only.`
        : "No clear reportable Structural Sound pattern was identified in the reviewed recordings.",
      patientText: detected ? STRUCTURAL_DETECTED_PATIENT_TEXT : null,
    }
    evidence.structuralSoundDetail = {
      ...deepCopy(structuralSource.evidence.structuralSoundDetail || {}),
      status,
      sourceRecordingId: structuralSource.recordingId,
      sourcePosition: position,
      finding: `Structural-sound detail is from the same ${
        position || "source"
      } recording selected for the exam-level finding.`,
    }
  } else {
    const noStructuralAccepted =
      contributors.length > 0 && !contributors.some((item) => domainsOf(item).structuralSound)
    // Accepted for Structural Sound, but nothing could be scored: the summary
    // says so, from the first such recording — grey, never a normal result
    const limitedSource = [...contributors]
      .filter((item) => domainsOf(item).structuralSound && item.evidence.structuralSoundSummary)
      .sort((a, b) => (Number(a.recordingSequence) || 0) - (Number(b.recordingSequence) || 0))[0]
    if (limitedSource) {
      const summary = deepCopy(limitedSource.evidence.structuralSoundSummary)
      const position = limitedSource.position || summary.sourcePosition || null
      limitedStructuralSourceId = limitedSource.recordingId
      evidence.structuralSoundSummary = {
        ...summary,
        observed: STRUCTURAL_NOT_ASSESSED_OBSERVED,
        status: "grey",
        sourceRecordingId: limitedSource.recordingId,
        sourcePosition: position,
        sourceRecordingIds: structuralSourceIds,
        sourceRecordings: structuralSourceRecords,
        sysCyclePct: null,
        diaCyclePct: null,
        maxCyclePct: null,
        dominantPhase: null,
        energyStrength: null,
        detailStatus: "unassessable_insufficient_scored_cycles",
        finding: STRUCTURAL_LIMITED_FINDING,
        patientText: STRUCTURAL_ASSESSMENT_LIMITED_PATIENT_TEXT,
        selectionRule: STRUCTURAL_LIMITED_RULE,
      }
      evidence.structuralSoundDetail = {
        observed: "Not assessed",
        status: "grey",
        sourceRecordingId: limitedSource.recordingId,
        sourcePosition: position,
        finding: "Structural-sound detail was unavailable because no scored structural-sound cycles were present.",
      }
    } else {
      evidence.structuralSoundSummary = {
        observed: STRUCTURAL_NOT_ASSESSED_OBSERVED,
        status: "grey",
        sourceRecordingId: null,
        sourcePosition: null,
        sourceRecordingIds: [],
        sourceRecordings: [],
        sysCyclePct: null,
        diaCyclePct: null,
        maxCyclePct: null,
        dominantPhase: null,
        energyStrength: null,
        energyValue: null,
        phaseStatus: "unavailable",
        patientText: null,
        providerText: null,
        finding: noStructuralAccepted
          ? NO_STRUCTURAL_SOUND_ACCEPTED_FINDING
          : "No contributing recording was structurally assessable.",
        selectionRule: EXAM_ROLLUP_STRUCTURAL_RULE,
      }
      evidence.structuralSoundDetail = {
        observed: "Not assessed",
        status: "grey",
        sourceRecordingId: null,
        sourcePosition: null,
        finding: "Repeated structural-sound detail unavailable.",
      }
    }
  }

  // ---- Candidate evidence ---------------------------------------------
  // Usable evidence of a domain the reviewer left unaccepted, on recordings
  // accepted for the other domain: carried beside the evidence blocks for
  // the report to offer as pending, never treated as accepted
  const timingCandidate = buildTimingCandidate(contributors)
  const structuralCandidate = buildStructuralSoundCandidate(contributors)
  if (timingCandidate || structuralCandidate) {
    pkg.candidateEvidence = {
      ...(timingCandidate ? { timing: timingCandidate } : {}),
      ...(structuralCandidate ? { structuralSound: structuralCandidate } : {}),
    }
  } else {
    delete pkg.candidateEvidence
  }

  // ---- Cycle Regularity, Signal Quality, S1/S2 -------------------------
  evidence.rhythmRegularity = rollUpCycleRegularity(evidence.rhythmRegularity, timing, reviewRequired)
  const reportSourceIds = [...new Set([...includedIds, ...(structuralSource ? [structuralSource.recordingId] : [])])]
  const reportSources = contributors.filter((item) => reportSourceIds.includes(item.recordingId))
  evidence.signalQuality = rollUpSignalQuality(evidence.signalQuality, reportSources)
  // Which recordings the report's evidence comes from, at exam level (the
  // per-recording package named only itself). No recording is "selected"
  // here: the roll-up is invariant to the one the reviewer had open.
  pkg.productUse = {
    ...(pkg.productUse || {}),
    bestHeardAtPolicy: "viewer_usable_position_and_hcp_selected_report_evidence",
  }
  pkg.reportEvidenceSource = {
    schema: "medaica.pcg.report_evidence_source.v1",
    policy: "best_heard_at_product_equivalent",
    selectionBasis:
      "Report evidence uses HCP-accepted usable Viewer positions; Timing may use multiple coherent accepted positions and Structural Sound uses the strongest reportable accepted position.",
    selectedRecordingId: null,
    selectedPosition: null,
    timingSourceRecordingIds: includedIds,
    structuralSoundSourceRecordingIds: structuralSource ? [structuralSource.recordingId] : [],
    reportableRecordingIds: reportSourceIds,
    reportableSources: reportSources.map((item) => ({
      recordingId: item.recordingId,
      position: item.position || null,
      timingAccepted: isIncluded(item.recordingId),
      structuralSoundAccepted: !!structuralSource && structuralSource.recordingId === item.recordingId,
    })),
    hcpSelected: reportSourceIds.length > 0,
  }
  const sourceS1s2 = structuralSource && structuralSource.evidence.s1s2IntensityBalance
  evidence.s1s2IntensityBalance = sourceS1s2
    ? {
        ...deepCopy(sourceS1s2),
        sourceRecordingId: structuralSource.recordingId,
        sourcePosition: structuralSource.position || sourceS1s2.sourcePosition || null,
        finding: `${
          sourceS1s2.finding || ""
        } Position-specific supporting evidence from the Structural Sound source; not averaged across sites.`.trim(),
      }
    : {
        ...(evidence.s1s2IntensityBalance || {}),
        observed: "Not assessed",
        status: "amber",
        sourceRecordingId: null,
        sourcePosition: null,
        finding: "S1/S2 intensity balance was not available from the Structural Sound source.",
        notAssessedReason: "no_credible_s1_s2_markers_for_structural_source",
      }

  // ---- Scope, chips, audit block -------------------------------------
  const positions = contributors.map((item) => item.position).filter(Boolean)
  const scope = {
    scope: "domain_accepted_exam_evidence",
    recordingCount: contributors.length,
    coverageStatus: "accepted_evidence_domains_only",
    coverageLabel: `${contributors.length} accepted recording${contributors.length === 1 ? "" : "s"}`,
    coverageNote:
      "Exam summary uses only the evidence the reviewing HCP accepted on each recording (Timing, Structural Sound, or both). Unreviewed and rejected recordings are excluded without being characterized as normal or abnormal.",
    confidencePolicy: CONFIDENCE_POLICY,
    standardPositionsRecorded: positions,
  }
  pkg.examScope = { ...(pkg.examScope || {}), ...scope }
  pkg.exam = { ...(pkg.exam || {}), ...scope, examId: pkg.examId || (pkg.exam && pkg.exam.examId) || null }
  const flagsFor = (item) => ({
    reviewStatus: item.reviewStatus || "none",
    explicitAiAgreement: item.reviewStatus === "accepted",
    includedForExamRollup: contributors.some((source) => source.recordingId === item.recordingId),
    acceptedForExamRollup: item.reviewStatus === "accepted",
    acceptedDomains:
      item.reviewStatus === "accepted" ? { ...domainsOf(item) } : { timing: false, structuralSound: false },
    timingEligible: timing.candidates.some((source) => source.recordingId === item.recordingId),
    timingIncluded: isIncluded(item.recordingId),
    timingDiscrepant: timing.discrepant.some((source) => source.recordingId === item.recordingId),
    halfDoubleSuspect: timing.halfDoubleIds.includes(item.recordingId),
    structuralSoundEligible: !!(
      item.evidence &&
      domainsOf(item).structuralSound &&
      structurallyAssessable(item.evidence)
    ),
    structuralSoundSource: !!structuralSource && structuralSource.recordingId === item.recordingId,
  })
  pkg.recordings = (pkg.recordings || []).map((chip) => {
    const item = all.find((candidate) => candidate.recordingId === chip.recordingId)
    return item ? { ...chip, ...flagsFor(item) } : chip
  })
  pkg.examRollup = {
    schema: EXAM_ROLLUP_SCHEMA,
    createdAt: now(),
    selectedRecordingIgnored: true,
    contributorRule,
    acceptedRecordingIds: all.filter((item) => item.reviewStatus === "accepted").map((item) => item.recordingId),
    contributingRecordingIds: contributors.map((item) => item.recordingId),
    excludedByReviewStatus: all
      .filter((item) => item.evidence && !contributors.some((source) => source.recordingId === item.recordingId))
      .map((item) => ({
        recordingId: item.recordingId,
        position: item.position || null,
        reviewStatus: item.reviewStatus || "none",
      })),
    excludedWithoutEvidence: all.filter((item) => !item.evidence).map((item) => item.recordingId),
    recordings: all.map((item) => ({
      recordingId: item.recordingId,
      position: item.position || null,
      recordingSequence: item.recordingSequence,
      heartRateBpm: heartRateBpm(item.evidence),
      pipelineHeartRateBpm: item.pipelineMetrics ? numberOrNull(item.pipelineMetrics.hrBpm) : null,
      hcpConfirmedBpm: confirmedBpmOf(item),
      // Where a timing-engine recording's timing came from
      ...evaluationRangeEntry(item),
      ...flagsFor(item),
    })),
    timing: {
      rule: EXAM_ROLLUP_TIMING_RULE,
      inputSet: timing.inputSet,
      noTimingAccepted: timing.noTimingAccepted,
      eligibleRecordingIds: timing.candidates.map((item) => item.recordingId),
      includedRecordingIds: includedIds,
      // Nothing accepted is ever left out; kept for readers of earlier roll-ups
      excludedRecordingIds: [],
      proposedClusterRecordingIds: timing.proposedCluster.map((item) => item.recordingId),
      proposedClusterRangeBpm: timing.proposedRangeBpm,
      discrepantRecordingIds: timing.discrepant.map((item) => item.recordingId),
      discrepantValuesBpm: timing.discrepant.map((item) => ({ recordingId: item.recordingId, valueBpm: item.bpm })),
      halfDoubleSuspectRecordingIds: timing.halfDoubleIds,
      halfDoubleWeakerMarkerProvenanceIds: timing.halfDoubleWeakerMarkerProvenanceIds,
      halfDoubleReviewFindings: timing.halfDoubleReviewFindings,
      markerProvenanceScore: timing.markerProvenanceScore,
      inferredMarkerPct: timing.inferredMarkerPct,
      // Recordings whose rate is the reviewer's confirmed value, and the one
      // confirmed value the summary resolved to (null when none or mixed)
      confirmedBpmRecordingIds: timing.confirmedRecordingIds,
      hcpConfirmedBpm: timing.hcpConfirmedBpm,
      acceptedSource: timing.acceptedSource,
      automatedSource: timing.automatedSource,
      automatedValueBpm: timing.automatedValueBpm,
      reviewRequired,
      reviewReasons: timing.reviewReasons,
      reviewReason: reviewRequired ? reviewReason : null,
      tiedBestCluster: timing.tiedBest,
      observed: timing.observed,
      valueBpm: timing.valueBpm,
      rangeBpm: timing.rangeBpm,
      sdRatio: timing.sdRatio,
      sysMs: timing.sysMs,
      diaMs: timing.diaMs,
      durationStatistic: timing.durationStatistic,
      pipelineCycleVariability: timing.rrCv,
    },
    cycleRegularity: {
      rule: "summarized over the recordings in the heart-rate summary",
      sourceRecordingIds: includedIds,
      observed: evidence.rhythmRegularity.observed,
    },
    signalQuality: {
      rule: "lowest signal quality among the recordings contributing evidence to the report",
      sourceRecordingIds: reportSourceIds,
      observed: evidence.signalQuality.observed,
      lowestRecordingId: evidence.signalQuality.lowestRecordingId || null,
      lowestGuideQuality: evidence.signalQuality.lowestGuideQuality || null,
    },
    structuralSound: {
      rule: EXAM_ROLLUP_STRUCTURAL_RULE,
      noStructuralAccepted: contributors.length > 0 && !contributors.some((item) => domainsOf(item).structuralSound),
      sourceRecordingId: structuralSource ? structuralSource.recordingId : null,
      sourcePosition: structuralSource ? structuralSource.position || null : null,
      status: structuralSource ? structuralSource.evidence.structuralSoundSummary.status : null,
      classification: evidence.structuralSoundSummary.status || null,
      maxCyclePct: numberOrNull(evidence.structuralSoundSummary.maxCyclePct),
      assessmentLimitedRecordingId: limitedStructuralSourceId,
      acceptedSources: structuralSourceRecords,
    },
    // Unaccepted evidence offered to the report as pending candidates
    candidateEvidence: {
      timingRecordingIds: timingCandidate ? timingCandidate.sourceRecordingIds : [],
      structuralSoundRecordingIds: structuralCandidate ? structuralCandidate.sourceRecordingIds : [],
    },
    // Machine-readable reasons behind a "Not assessed" domain
    domainAvailability: {
      timingAccepted: !timing.noTimingAccepted && contributors.length > 0,
      structuralSoundAccepted: contributors.some((item) => domainsOf(item).structuralSound),
      timingEmptyReason: timing.noTimingAccepted ? "no_recording_accepted_for_timing" : null,
      structuralSoundEmptyReason:
        contributors.length > 0 && !contributors.some((item) => domainsOf(item).structuralSound)
          ? "no_recording_accepted_for_structural_sound"
          : null,
    },
  }
  pkg.screening = pkg.screening || {}
  pkg.screening.examScope = scope
  pkg.screening.timingSummary = {
    ...(pkg.screening.timingSummary || {}),
    bpm: timing.valueBpm,
    observed: timing.observed,
    reviewRequired,
  }
  return pkg
}
